import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Flower, MapPin, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import Layout from '../layout/Layout';
import PageHeader from '../layout/PageHeader';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';
import { actionClasses } from '../../config/designSystem';

const AboutPage = () => {
  return (
    <Layout>
      <PageHeader
        title="About FlowBerry"
        description="Premium roses, crafted with love in Glendale, California"
        videoSrc="/images/about_anime.mp4"
      />

      {/* Story */}
      <section className="relative overflow-hidden bg-[#F7F2EE] py-16 md:py-20">
        <div className="pointer-events-none absolute -left-28 top-24 h-72 w-72 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none rounded-[1.65rem] border border-rose-200/70 bg-white/68 p-6 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl sm:p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                <Heart className="w-6 h-6 text-[#FB7185]" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] m-0">Our Story</h2>
            </div>

            <p className="text-stone-600 leading-relaxed mb-6">
              FlowBerry was born from a simple belief: every moment worth celebrating deserves 
              the beauty of premium roses. Based in Glendale, California, we bring the finest 
              rose arrangements to doorsteps across Los Angeles.
            </p>

            <p className="text-stone-600 leading-relaxed mb-6">
              We source only the highest quality roses — each stem selected for its perfect 
              bloom, rich color, and lasting freshness. Whether you're celebrating a birthday, 
              anniversary, or simply want to brighten someone's day, our arrangements are 
              crafted to make every occasion unforgettable.
            </p>

            <div className="flex items-center gap-3 mb-8 mt-12">
              <div className="w-12 h-12 rounded-xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                <Flower className="w-6 h-6 text-[#FB7185]" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] m-0">Our Promise</h2>
            </div>

            <ul className="space-y-4 text-stone-600">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#FB7185] mt-2.5 flex-shrink-0 shadow-[0_0_12px_rgba(251,113,133,0.45)]" />
                <span><strong className="text-[#3A2A2A]">Premium Quality:</strong> Only the finest roses make it into our bouquets</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#FB7185] mt-2.5 flex-shrink-0 shadow-[0_0_12px_rgba(251,113,133,0.45)]" />
                <span><strong className="text-[#3A2A2A]">Freshly Arranged:</strong> Every bouquet is crafted to order</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#FB7185] mt-2.5 flex-shrink-0 shadow-[0_0_12px_rgba(251,113,133,0.45)]" />
                <span><strong className="text-[#3A2A2A]">Personal Service:</strong> Direct communication via WhatsApp for a seamless experience</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#FB7185] mt-2.5 flex-shrink-0 shadow-[0_0_12px_rgba(251,113,133,0.45)]" />
                <span><strong className="text-[#3A2A2A]">Beautiful Presentation:</strong> Ribbon bow included with every order, premium wrap available</span>
              </li>
            </ul>

            <div className="flex items-center gap-3 mb-8 mt-12">
              <div className="w-12 h-12 rounded-xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                <MapPin className="w-6 h-6 text-[#FB7185]" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] m-0">Serving Los Angeles</h2>
            </div>

            <p className="text-stone-600 leading-relaxed">
              From our home base in Glendale, we deliver beautiful rose bouquets throughout 
              Los Angeles and LA County. Whether you're in Hollywood, Pasadena, Downtown LA, 
              or anywhere in between, we'll bring the flowers to you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-[2] isolate overflow-visible bg-gradient-to-b from-[#FFF8F5] to-[#F7F2EE] py-16 md:py-20">
        <div className="section-divider-rose absolute top-0 left-0 right-0 z-[30]" aria-hidden="true" />
        <div className="pointer-events-none absolute bottom-[-260px] right-0 z-[1] hidden translate-x-[30px] md:block" aria-hidden="true">
          <img
            src="/images/about_flow.png?v=2"
            alt=""
            className="h-[760px] w-auto max-w-none object-contain object-bottom-right lg:h-[820px]"
          />
        </div>
        <div className="relative z-[20] max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#3A2A2A] mb-4">
            Let's Create Something Beautiful
          </h2>
          <p className="text-stone-600 mb-8 max-w-xl mx-auto">
            Build your perfect bouquet or reach out for a personalized arrangement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/build">
              <Button size="lg" className={`w-full sm:w-auto px-8 ${actionClasses.primary}`}>
                Build Your Bouquet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={getDefaultInquiryURL()} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto px-8 border-[#FB7185]/30 bg-white/62 text-[#3A2A2A] hover:bg-white/85 hover:text-[#FB7185] shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat with Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
